import type { TextStyleOptions } from 'pixi.js';

/** Portrait design space (9:16). Scenes lay out in these coordinates; the world is scaled to fit. */
export const DESIGN_W = 720;
export const DESIGN_H = 1280;

/**
 * Wood + gold palette, mirroring the Unity client (dark wood backdrop, gold buttons with white
 * labels per `ButtonColorSetter`). Keys are unchanged from the original green theme, so every scene
 * and shared widget that reads `COLORS.*` re-skins automatically.
 */
export const COLORS = {
  // Wood-toned background fallbacks (used only if background.jpg fails to load).
  bgTop: 0x4a2f1c,
  bgMid: 0x281609,
  bgBottom: 0x140a04,
  // Warm dark panels that let the wood show through (Panel is drawn slightly translucent).
  panel: 0x1c130a,
  panelSoft: 0x140d06,
  panelBorder: 0x8a6d2a, // muted gold border
  // Unity gold action color + its hover/disabled tints (ButtonColorSetter.cs).
  accent: 0xecbb34, // #ECBB34 normal
  accentDark: 0x896d22, // #896D22 highlighted/pressed
  accentHover: 0x896d22,
  accentDisabled: 0x635634, // #635634 disabled
  accentText: 0xffffff, // white button labels on colored (green/red) fills, via a drop shadow
  buttonTextDark: 0x3a2708, // dark-brown label on gold buttons (the real game uses dark-on-gold)
  text: 0xf3e9d2,
  textDim: 0xc9b48a,
  danger: 0xe74c3c,
  gold: 0xecbb34, // real gold now (balance highlight)
  inputBg: 0x0e0904,
  white: 0xffffff,
  // Dark jewel tones for identifying games without leaving the wood-and-gold design language.
  gameHokm: 0x4a1f26, // muted oxblood
  gameBackgammon: 0x16384a, // deep petrol blue
  gameBlackjack: 0x193d2a, // dark casino green
  // Lobby design-language additions (match the real game's wood-on-spotlight look).
  pillBlack: 0x0b0d10, // black glossy info-pill body (username/balance/online)
  darkPill: 0x101418, // dark glossy pill used by the table/overlays (Ready, result, bet, tray, chat)
  scrim: 0x000000, // base color of every dim/scrim overlay
  fieldBorder: 0x5a4424, // softer text-field border (dimmer than panelBorder)
  sliderTrack: 0xcfc7b6, // light warm-gray slider track
};

/**
 * Shared design tokens. These name the magic numbers that were previously hardcoded per component,
 * so radii/borders/scrims/spacing stay consistent and can be tuned in one place. Values match the
 * existing rendered output (so adopting them is byte-identical) except where a comment notes a tweak.
 */
export const RADIUS = { sm: 8, field: 10, control: 12, button: 16, panel: 18 } as const;
export const BORDER = { thin: 1.5, base: 2, thick: 3 } as const;
export const SCRIM = { modal: 0.72, dialog: 0.62, list: 0.4 } as const; // list was 0.35 → 0.40
export const SPACING = {
  gutter: 40, // standard page side margin (content width DESIGN_W - 80 = 640)
  gutterSm: 24, // list-page side margin (content width DESIGN_W - 48 = 672)
  gap: 20, // gap between two side-by-side bottom buttons
  rowH: 88, // vertical pitch of a label+control form row
  bottomBackY: DESIGN_H - 80, // back-button baseline (the primary action sits a gap above it; see bottomPrimaryY())
} as const;
/** Portrait-page sizes target ~16–17 CSS px body text at 390px-wide iPhones. */
export const TYPE = { title: 42, heading: 34, body: 30, label: 26, micro: 24 } as const;
export const MOBILE = {
  controlH: 88,
  secondaryH: 80,
  compactH: 72,
  sectionGap: 24,
  bottomInset: 16,
} as const;

export const FONT_FAMILY = 'Vazirmatn, Tahoma, system-ui, sans-serif';

export interface TextOpts {
  size?: number;
  fill?: number;
  weight?: TextStyleOptions['fontWeight'];
  align?: TextStyleOptions['align'];
}

/** Builds a Pixi text style with the Persian font and sensible RTL defaults. */
export function textStyle(opts: TextOpts = {}): TextStyleOptions {
  return {
    fontFamily: FONT_FAMILY,
    fontSize: opts.size ?? 24,
    fill: opts.fill ?? COLORS.text,
    fontWeight: opts.weight ?? '400',
    align: opts.align ?? 'right',
  };
}
